import type { JSX } from 'react';
import { TableIcon, TriangleAlertIcon, ZapIcon } from 'lucide-react';
import { PROJECT_TEMPLATES, type ProjectTemplate } from '@mockd/shared';
import { LoadingLabel } from '../common/Cursor';

interface TemplateSelectorProps {
  onSelect: (template: ProjectTemplate) => void;
  isLoading?: boolean;
  loadingTemplateId?: string | null;
  size?: 'standard' | 'hero';
}

const TEMPLATE_ICONS: Record<string, JSX.Element> = {
  'rest-crud': <TableIcon className="size-6" />,
  'webhook-receiver': <ZapIcon className="size-6" />,
  'error-simulation': <TriangleAlertIcon className="size-6" />,
};

export function TemplateSelector({
  onSelect,
  isLoading,
  loadingTemplateId,
  size = 'standard',
}: TemplateSelectorProps) {
  const isHero = size === 'hero';

  const gridClass = isHero
    ? 'grid grid-cols-1 sm:grid-cols-3 gap-4'
    : 'grid grid-cols-1 sm:grid-cols-3 gap-3';

  const cardClass = isHero
    ? 'card bg-base-100 border border-base-300 hover:border-primary hover:-translate-y-0.5 transition-all text-left p-6 disabled:opacity-50 disabled:cursor-not-allowed'
    : 'card bg-base-100 border border-base-300 hover:border-primary hover:-translate-y-0.5 transition-all text-left p-4 disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div className={gridClass}>
      {PROJECT_TEMPLATES.map((template) => {
        const isThisLoading = isLoading && loadingTemplateId === template.id;
        const icon = TEMPLATE_ICONS[template.id] ?? TEMPLATE_ICONS['rest-crud'];

        return (
          <button
            key={template.id}
            onClick={() => onSelect(template)}
            disabled={isLoading}
            className={cardClass}
          >
            <div className="flex flex-col gap-3">
              <div className="bg-primary/10 text-primary rounded-field p-3 w-12 h-12 flex items-center justify-center">
                {icon}
              </div>
              <div>
                <span className="block font-semibold text-base text-base-content">
                  {isThisLoading ? <LoadingLabel>Creating</LoadingLabel> : template.name}
                </span>
                <p className="text-sm text-base-content/70 mt-1">
                  {template.description}
                </p>
                <p className="text-xs text-base-content/70 mt-3">
                  {template.endpoints.length} {template.endpoints.length === 1 ? 'endpoint' : 'endpoints'}
                </p>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
